/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { ContentCardOrientation } from '@airasia-phoenix/react-native/widgets/HorizontalContentCardView/ContentDetailsView';

import { Constants } from './constants';

import { LabelType } from '@airasia-phoenix/react-native/components/LabelView/types';
import { pd } from '@airasia-rn/ui';
import {
  HotelDealsItemAPIResponse,
  HotelDealsCarouselItemProps,
} from '../../types';
import { getSearchReviewCountLabel } from '../../utils/reviews';

export const mapToVerticalContentCardProps = (
  data: HotelDealsItemAPIResponse[],
  t: (key: string, options?: { count?: number }) => string = (key) => key,
  onPress?: (item: HotelDealsItemAPIResponse, index: number) => void,
): HotelDealsCarouselItemProps[] => {
  //@ts-expect-error return is not working.

  return data.map((item, index) => {
    const hasPrice = !!(item.priceText?.length && item.price?.length);
    const getFormattedPrice = (priceStr?: string): string | undefined => {
      if (!priceStr) return undefined;
      const parts = priceStr.split(' ');
      const currency = parts[0];
      const value = parts[1];
      const numericValue = Math.ceil(Number(value));
      return !isNaN(numericValue) ? `${currency} ${numericValue}` : priceStr;
    };
    const displayPrice = getFormattedPrice(item.price);

    const displayStrikethrough = item.details?.strikethroughprice
      ? Math.ceil(Number(item.details.strikethroughprice))
      : undefined;

    return {
      onPress: () => onPress?.(item, index),
      model: item,
      favouriteIcon: false,
      imageHeight: Constants.IMAGE_HEIGHT,
      imageWidth: Constants.IMAGE_WIDTH,
      ...(item.location && {
        supportingInfo: {
          supportingIcon: { icon: pd.icon.main.communication.location_on },
          supportingText: item.location,
        },
      }),
      image: {
        source: { uri: item.image },
        cacheProps: {
          priority: 'normal',
          cachePolicy: 'immutable',
        },
        onError: () => {},
      },
      contentDetailsPropsValues: {
        orientation: ContentCardOrientation.VERTICAL,
        primaryText: item.title ?? '',
        mainLabel: {
          text:
            item.rating && item.maxRating
              ? `${item.rating}/ ${item.maxRating}`
              : undefined,
          type: LabelType.FILLED_BLUE,
        },
        subLabel: {
          text: item.discount || '',
          type: LabelType.TONED_GREEN,
        },
        labelSupportingText:
          item.reviewCount && Number(item.reviewCount) > 0
            ? `(${getSearchReviewCountLabel(item.reviewCount, t)})`
            : undefined,
        secondaryText: item.subTitle ?? '',
        valueEnding: hasPrice ? item.priceText : undefined,
        value: hasPrice ? displayPrice : undefined,
        valueSlash:
          item?.details?.currency && displayStrikethrough
            ? `${item.details.currency} ${displayStrikethrough}`
            : undefined,
      },
    };
  });
};

export const getHotelDealsCarouselSignature = (
  cards: HotelDealsCarouselItemProps[],
) =>
  cards
    .map((card) => {
      const model = card.model as HotelDealsItemAPIResponse | undefined;
      const modelWithId = model as
        | (HotelDealsItemAPIResponse & { id?: string })
        | undefined;
      return (
        model?.details?.productid ||
        modelWithId?.id ||
        card.contentDetailsPropsValues?.primaryText ||
        ''
      );
    })
    .join('|');
